import { showConfirm } from '@/services/dialogService'
import { formatByteSize } from '@/services/formattingService'

export interface StorageQuotaInfo {
  quota: number
  usage: number
  available: number
  usagePercent: number
}

export class StorageQuotaService {
  private static readonly WARNING_THRESHOLD = 0.55 // 85%
  private static readonly CRITICAL_THRESHOLD = 0.95 // 95%
  private static readonly WARNING_THROTTLE_MINUTES = 15

  private lastWarningShown: number = 0
  private lastCriticalShown: number = 0

  /**
   * Get current storage quota information
   */
  async getStorageQuota(): Promise<StorageQuotaInfo | null> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate()
      const quota = estimate.quota || 0
      const usage = estimate.usage || 0
      const available = quota - usage
      const usagePercent = quota > 0 ? usage / quota : 0

      return {
        quota,
        usage,
        available,
        usagePercent,
      }
    }
    return null
  }

  /**
   * Check if there's sufficient storage available for a given size
   */
  async checkStorageAvailable(requiredBytes: number): Promise<boolean> {
    const quotaInfo = await this.getStorageQuota()

    if (!quotaInfo) {
      // If quota API is not available, assume storage is available
      return true
    }

    return quotaInfo.available >= requiredBytes
  }

  /**
   * Check storage quota and show warning/error dialogs if needed
   * Returns true if operation should proceed, false if it should stop
   */
  async checkQuotaBeforeOperation(requiredBytes: number = 0): Promise<boolean> {
    const quotaInfo = await this.getStorageQuota()

    if (!quotaInfo) {
      // Storage quota API not available, allow operation to proceed
      return true
    }

    const { quota, usage, available, usagePercent } = quotaInfo

    // Check if there's enough space for the operation (highest priority)
    if (requiredBytes > 0 && available < requiredBytes) {
      const confirmed = await showConfirm({
        title: 'Storage Full',
        message: `Not enough storage space available. Required: ${formatByteSize(requiredBytes)}, Available: ${formatByteSize(available)}.

Please free up some space on your device.`,
        confirmText: 'Continue Anyway',
        cancelText: 'Cancel',
        variant: 'danger',
      })

      if (!confirmed) {
        return false
      }
      // If user chooses to continue anyway, don't show additional warnings
      return true
    }

    // Check if we're approaching quota limits (only if we didn't already show storage full dialog)
    if (usagePercent >= StorageQuotaService.CRITICAL_THRESHOLD) {
      // Always show critical warnings (they block operations)
      const confirmed = await showConfirm({
        title: 'Storage Almost Full',
        message: `Storage is ${Math.round(usagePercent * 100)}% full (${formatByteSize(usage)} of ${formatByteSize(quota)} used).

The application may not function properly when storage is full. Consider freeing up space on your device.`,
        confirmText: 'Understand, continue',
        cancelText: 'Cancel',
        variant: 'warning',
      })

      if (!confirmed) {
        return false
      }
    } else if (usagePercent >= StorageQuotaService.WARNING_THRESHOLD) {
      // Throttle warning messages to once every 15 minutes
      const now = Date.now()
      const throttleMs = StorageQuotaService.WARNING_THROTTLE_MINUTES * 60 * 1000

      if (now - this.lastWarningShown > throttleMs) {
        this.lastWarningShown = now

        await showConfirm({
          title: 'Storage Usage Warning',
          message: `Storage usage is high (${Math.round(usagePercent * 100)}% full). Consider freeing up space on your device.`,
          confirmText: 'OK',
          cancelText: undefined,
          variant: 'warning',
        })
      }
    }

    return true
  }

  /**
   * Handle quota exceeded errors from IndexedDB operations
   */
  async handleQuotaExceededError(): Promise<void> {
    const quotaInfo = await this.getStorageQuota()

    const message = quotaInfo
      ? `Storage quota exceeded. Currently using ${formatByteSize(quotaInfo.usage)} of ${formatByteSize(quotaInfo.quota)}. Consider freeing up space on your device.`
      : 'Storage quota exceeded. Consider freeing up space on your device.'

    await showConfirm({
      title: 'Storage Quota Exceeded',
      message,
      confirmText: 'OK',
      cancelText: undefined,
      variant: 'danger',
    })
  }

  /**
   * Check if an error is a quota exceeded error
   */
  isQuotaExceededError(error: unknown): boolean {
    if (!error) return false

    const errorMessage = error instanceof Error ? error.message : String(error)
    const errorName = error instanceof Error ? error.name : ''

    return (
      errorName === 'QuotaExceededError' ||
      errorMessage.toLowerCase().includes('quota') ||
      errorMessage.toLowerCase().includes('storage') ||
      errorMessage.toLowerCase().includes('exceeded')
    )
  }
}

// Create and export singleton instance
export const storageQuotaService = new StorageQuotaService()
